import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  MessageCircle, LogOut, MapPin, Phone, Building2, User,
  Pencil, X, Check, KeyRound, ChevronDown, ChevronUp, Mail,
} from 'lucide-react';

const inputCls = 'w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30';

export default function PerfilCliente() {
  const { user, logout } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Change password
  const [showPass, setShowPass] = useState(false);
  const [passForm, setPassForm] = useState({ atual: '', nova: '', confirma: '' });
  const [savingPass, setSavingPass] = useState(false);
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/clientes/me'),
      api.get('/empresa').catch(() => ({ data: {} })),
    ]).then(([p, e]) => {
      setPerfil(p.data);
      setEmpresa(e.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startEdit = () => {
    setForm({
      nome_contato: perfil?.nome_contato || '',
      nome_empresa: perfil?.nome_empresa || '',
      whatsapp: perfil?.whatsapp || '',
      cep: perfil?.cep || '',
      logradouro: perfil?.logradouro || '',
      numero: perfil?.numero || '',
      bairro: perfil?.bairro || '',
      cidade: perfil?.cidade || '',
      uf: perfil?.uf || '',
      ponto_referencia: perfil?.ponto_referencia || '',
    });
    setSaveError('');
    setSaveSuccess('');
    setEditing(true);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCep = async (val) => {
    set('cep', val);
    const digits = val.replace(/\D/g, '');
    if (digits.length === 8) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setForm(f => ({ ...f, logradouro: data.logradouro, bairro: data.bairro, cidade: data.localidade, uf: data.uf }));
        }
      } catch { /* ignore */ }
    }
  };

  const handleSave = async () => {
    if (!form.nome_contato.trim()) { setSaveError('Nome de contato é obrigatório'); return; }
    setSaving(true);
    setSaveError('');
    try {
      await api.put('/clientes/me', form);
      const res = await api.get('/clientes/me');
      setPerfil(res.data);
      setEditing(false);
      setSaveSuccess('Perfil atualizado com sucesso!');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Erro ao salvar. Tente novamente.');
    } finally { setSaving(false); }
  };

  const handlePassSave = async () => {
    if (!passForm.atual || !passForm.nova) { setPassError('Preencha todos os campos'); return; }
    if (passForm.nova !== passForm.confirma) { setPassError('As senhas não coincidem'); return; }
    if (passForm.nova.length < 6) { setPassError('Nova senha deve ter pelo menos 6 caracteres'); return; }
    setSavingPass(true);
    setPassError('');
    try {
      await api.put('/clientes/me/senha', { senha_atual: passForm.atual, nova_senha: passForm.nova });
      setPassForm({ atual: '', nova: '', confirma: '' });
      setShowPass(false);
      setPassSuccess('Senha alterada com sucesso!');
      setTimeout(() => setPassSuccess(''), 4000);
    } catch (err) {
      setPassError(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally { setSavingPass(false); }
  };

  const handleWhatsApp = () => {
    let numero = empresa?.whatsapp?.replace(/\D/g, '') || '';
    if (!numero) { alert('Número de WhatsApp da empresa não cadastrado.'); return; }
    if (numero.startsWith('55') && numero.length > 11) numero = numero.slice(2);
    const nome = perfil?.nome_empresa || perfil?.nome_contato || user?.nome || '';
    const msg = encodeURIComponent(`Olá, sou ${nome} e preciso de ajuda.`);
    window.open(`https://wa.me/+55${numero}?text=${msg}`, '_blank');
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="pb-8 bg-brand-bg min-h-screen" data-testid="perfil-cliente">

      {/* Header */}
      <div className="bg-brand-primary px-5 pt-12 pb-6 text-white relative">
        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <User className="w-8 h-8 text-white" strokeWidth={1.5} />
        </div>
        <p className="text-center font-heading text-xl font-bold">
          {perfil?.nome_empresa || perfil?.nome_contato || user?.nome}
        </p>
        {perfil?.nome_empresa && (
          <p className="text-center font-body text-sm opacity-80 mt-0.5">{perfil.nome_contato}</p>
        )}
        {!editing && (
          <button
            onClick={startEdit}
            className="absolute top-12 right-5 flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-white text-xs font-dashboard font-semibold hover:bg-white/30 transition-colors"
            data-testid="edit-perfil-btn"
          >
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
        )}
      </div>

      <div className="px-4 pt-5 space-y-4">

        {/* Feedback banners */}
        {saveSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2" data-testid="save-success-msg">
            <Check className="w-4 h-4 flex-shrink-0" /> {saveSuccess}
          </div>
        )}
        {passSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
            <Check className="w-4 h-4 flex-shrink-0" /> {passSuccess}
          </div>
        )}

        {editing ? (
          /* ══ MODO EDIÇÃO ══ */
          <div className="space-y-4" data-testid="edit-mode">
            {saveError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="save-error-msg">
                {saveError}
              </div>
            )}

            {/* Dados pessoais */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-brand-border">
                <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide">Dados Pessoais</p>
              </div>
              <div className="px-4 py-4 space-y-3">
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">Nome do Responsável *</label>
                  <input className={inputCls} value={form.nome_contato} onChange={e => set('nome_contato', e.target.value)} data-testid="field-nome-contato" />
                </div>
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">Nome da Empresa / Estabelecimento</label>
                  <input className={inputCls} value={form.nome_empresa} onChange={e => set('nome_empresa', e.target.value)} data-testid="field-nome-empresa" placeholder="Deixe em branco se não tiver" />
                </div>
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">WhatsApp</label>
                  <input className={inputCls} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="(51) 99999-9999" data-testid="field-whatsapp" />
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-brand-border">
                <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide">Endereço de Entrega</p>
              </div>
              <div className="px-4 py-4 space-y-3">
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">CEP</label>
                  <input className={inputCls} value={form.cep} onChange={e => handleCep(e.target.value)} maxLength={9} placeholder="00000-000" data-testid="field-cep" />
                </div>
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">Logradouro</label>
                  <input className={inputCls} value={form.logradouro} onChange={e => set('logradouro', e.target.value)} data-testid="field-logradouro" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Nº</label>
                    <input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} data-testid="field-numero" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Bairro</label>
                    <input className={inputCls} value={form.bairro} onChange={e => set('bairro', e.target.value)} data-testid="field-bairro" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Cidade</label>
                    <input className={inputCls} value={form.cidade} onChange={e => set('cidade', e.target.value)} data-testid="field-cidade" />
                  </div>
                  <div>
                    <label className="block text-xs text-brand-text-muted font-body mb-1">UF</label>
                    <input className={inputCls} value={form.uf} onChange={e => set('uf', e.target.value)} maxLength={2} data-testid="field-uf" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-brand-text-muted font-body mb-1">Ponto de Referência</label>
                  <input className={inputCls} value={form.ponto_referencia} onChange={e => set('ponto_referencia', e.target.value)} data-testid="field-ponto-ref" />
                </div>
              </div>
            </div>

            {/* Botões Cancelar / Salvar */}
            <div className="flex gap-3 pb-2">
              <button
                onClick={() => { setEditing(false); setSaveError(''); }}
                className="flex-1 py-3.5 rounded-2xl border-2 border-brand-border text-brand-text font-dashboard font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4" /> Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3.5 rounded-2xl btn-primary font-dashboard font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-perfil-btn"
              >
                <Check className="w-4 h-4" />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>

        ) : (
          /* ══ MODO VISUALIZAÇÃO ══ */
          <>
            {/* Dados */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-brand-border">
                <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide">Dados</p>
              </div>
              <div className="divide-y divide-brand-border">
                <InfoRow icon={Mail} label="E-mail" value={user?.email} />
                {perfil?.whatsapp && <InfoRow icon={Phone} label="WhatsApp" value={perfil.whatsapp} />}
                {perfil?.turno && <InfoRow icon={Building2} label="Turno de Entrega" value={perfil.turno} />}
              </div>
            </div>

            {/* Endereço */}
            {perfil?.endereco_formatado && (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-brand-border">
                  <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide">Endereço de Entrega</p>
                </div>
                <div className="px-4 py-3 flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-accent-light rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-brand-primary" />
                  </div>
                  <p className="text-sm font-body text-brand-text leading-relaxed">{perfil.endereco_formatado}</p>
                </div>
              </div>
            )}

            {/* Alterar Senha */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <button
                onClick={() => { setShowPass(!showPass); setPassError(''); }}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-left"
                data-testid="toggle-senha-btn"
              >
                <div className="w-8 h-8 bg-brand-accent-light rounded-full flex items-center justify-center flex-shrink-0">
                  <KeyRound className="w-4 h-4 text-brand-primary" />
                </div>
                <span className="flex-1 text-sm font-dashboard font-medium text-brand-text">Alterar Senha</span>
                {showPass ? <ChevronUp className="w-4 h-4 text-brand-text-muted" /> : <ChevronDown className="w-4 h-4 text-brand-text-muted" />}
              </button>
              {showPass && (
                <div className="px-4 pb-4 space-y-3 border-t border-brand-border pt-3">
                  {passError && <p className="text-red-600 text-sm font-body" data-testid="pass-error">{passError}</p>}
                  <div>
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Senha atual</label>
                    <input type="password" className={inputCls} value={passForm.atual} onChange={e => setPassForm(f => ({ ...f, atual: e.target.value }))} data-testid="field-senha-atual" />
                  </div>
                  <div>
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Nova senha</label>
                    <input type="password" className={inputCls} value={passForm.nova} onChange={e => setPassForm(f => ({ ...f, nova: e.target.value }))} data-testid="field-senha-nova" />
                  </div>
                  <div>
                    <label className="block text-xs text-brand-text-muted font-body mb-1">Confirmar nova senha</label>
                    <input type="password" className={inputCls} value={passForm.confirma} onChange={e => setPassForm(f => ({ ...f, confirma: e.target.value }))} data-testid="field-senha-confirma" />
                  </div>
                  <button
                    onClick={handlePassSave}
                    disabled={savingPass}
                    className="w-full py-3 rounded-xl btn-primary font-dashboard font-semibold text-sm disabled:opacity-60"
                    data-testid="save-senha-btn"
                  >
                    {savingPass ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="space-y-3 pt-2">
              <button
                onClick={handleWhatsApp}
                className="w-full py-4 rounded-2xl bg-green-500 text-white font-dashboard font-semibold text-sm flex items-center justify-center gap-2 hover:bg-green-600 transition-colors"
                data-testid="suporte-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Falar com Suporte via WhatsApp
              </button>
              <button
                onClick={logout}
                className="w-full py-4 rounded-2xl border-2 border-red-200 text-red-600 font-dashboard font-semibold text-sm flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Sair da conta
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="w-8 h-8 bg-brand-accent-light rounded-full flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-brand-primary" />
      </div>
      <div>
        <p className="text-xs text-brand-text-muted font-body">{label}</p>
        <p className="text-sm font-body text-brand-text">{value}</p>
      </div>
    </div>
  );
}
